import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { AlertErrorIcon } from '@/components/icon'
import { SimpleLogoPageContainer } from '@/components/shared/SimpleLogoPageContainer'
import {
  Badge,
  Box,
  Button,
  Center,
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
  FieldRoot,
  Flex,
  Grid,
  Heading,
  HStack,
  Input,
  PasswordInput,
  Stack,
  Text,
} from '@/components/ui'
import { ERROR_MESSAGES } from '@/constants/errorMessages'
import { PASSWORD_FORMAT_REGEX } from '@/constants/passwordFormatRegex'
import { ApiError, useMutation } from '@/lib/api'
import { confirmForgotPassword } from '@/lib/auth'

const resetPasswordFormSchema = z
  .object({
    code: z.string().min(1, ERROR_MESSAGES.codeRequired),
    newPassword: z
      .string()
      .min(1, ERROR_MESSAGES.passwordRequired)
      .min(8, ERROR_MESSAGES.passwordFormat)
      .regex(PASSWORD_FORMAT_REGEX, ERROR_MESSAGES.passwordFormat),
    confirmPassword: z
      .string()
      .min(1, ERROR_MESSAGES.passwordRequired)
      .min(8, ERROR_MESSAGES.passwordFormat)
      .regex(PASSWORD_FORMAT_REGEX, ERROR_MESSAGES.passwordFormat),
  })
  .refine(data => data.newPassword === data.confirmPassword, {
    message: ERROR_MESSAGES.passwordMismatch,
    path: ['confirmPassword'],
  })

type FormValue = z.infer<typeof resetPasswordFormSchema>

type Props = {
  email: string
  onGoNext: () => void
}

export const ResetPassword = ({ email, onGoNext }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<FormValue>({
    mode: 'onBlur',
    resolver: zodResolver(resetPasswordFormSchema),
  })

  const [apiError, setApiError] = useState<ApiError>()

  const { request, isPending } = useMutation(confirmForgotPassword, {
    onError: setApiError,
    onSuccess: () => {
      onGoNext()
    },
  })

  const passwordMismatchError =
    errors.confirmPassword?.message === ERROR_MESSAGES.passwordMismatch
      ? errors.confirmPassword.message
      : undefined

  return (
    <SimpleLogoPageContainer>
      <Stack boxSize="full" gap="12">
        <Stack align="center">
          <Heading>パスワードの再設定</Heading>
          <Text
            textAlign="center"
            whiteSpace="pre-wrap"
          >{`登録メールアドレスに確認コードを送信しました。\nメール本文の確認コードを入力し、新しいパスワードを設定してください。`}</Text>
        </Stack>
        <Box w="full" maxW="800px">
          <form
            onSubmit={handleSubmit(async data => {
              await request({
                email,
                code: data.code,
                newPassword: data.newPassword,
              })
            })}
          >
            <Stack gap="12">
              <Grid
                rowGap="7"
                columnGap="6"
                templateColumns={{ base: '1fr', desktop: 'max-content 1fr' }}
              >
                <FieldRoot
                  display="grid"
                  alignItems="start"
                  gridTemplateColumns="subgrid"
                  gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                  gap={{ base: '1', desktop: '4' }}
                  invalid={!!errors.code}
                >
                  <Flex justify="space-between" gap="2" align="center">
                    <FieldLabel
                      fontSize="md"
                      fontWeight="normal"
                      color="text.sub"
                      h="10"
                    >
                      確認コード
                    </FieldLabel>
                    <Badge variant="subtle" colorPalette="red" h="20px">
                      必須
                    </Badge>
                  </Flex>
                  <Stack gap="0.5">
                    <Input {...register('code')} type="tel" />
                    <FieldErrorText>{errors.code?.message}</FieldErrorText>
                  </Stack>
                </FieldRoot>
                <FieldRoot
                  display="grid"
                  alignItems="start"
                  gridTemplateColumns="subgrid"
                  gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                  gap={{ base: '1', desktop: '4' }}
                  invalid={!!errors.newPassword}
                >
                  <Flex justify="space-between" gap="6" align="center">
                    <FieldLabel
                      fontSize="md"
                      fontWeight="normal"
                      color="text.sub"
                      h="10"
                    >
                      新しいパスワード
                    </FieldLabel>
                    <Badge variant="subtle" colorPalette="red" h="20px">
                      必須
                    </Badge>
                  </Flex>
                  <Stack gap="0.5">
                    <PasswordInput
                      {...register('newPassword')}
                      autoComplete="new-password"
                      placeholder="パスワードを入力してください"
                    />
                    {!errors.newPassword && (
                      <FieldHelperText>
                        8文字以上の半角英数字記号（大文字、小文字をそれぞれ1文字以上ずつ含める）
                      </FieldHelperText>
                    )}
                    {errors.newPassword && (
                      <FieldErrorText>
                        {errors.newPassword?.message}
                      </FieldErrorText>
                    )}
                  </Stack>
                </FieldRoot>
                <FieldRoot
                  display="grid"
                  alignItems="start"
                  gridTemplateColumns="subgrid"
                  gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                  gap={{ base: '1', desktop: '4' }}
                  invalid={!!errors.confirmPassword && !passwordMismatchError}
                >
                  <Flex justify="space-between" align="center">
                    <FieldLabel
                      fontSize="md"
                      fontWeight="normal"
                      color="text.sub"
                      h="10"
                      whiteSpace={{ base: 'none', desktop: 'pre-wrap' }}
                    >
                      {`新しいパスワード\n（確認）`}
                    </FieldLabel>
                    <Badge variant="subtle" colorPalette="red" h="20px">
                      必須
                    </Badge>
                  </Flex>
                  <Stack gap="0.5">
                    <PasswordInput
                      {...register('confirmPassword')}
                      autoComplete="new-password"
                      placeholder="パスワードを入力してください"
                    />
                    {(!!passwordMismatchError || !errors.confirmPassword) && (
                      <FieldHelperText>
                        8文字以上の半角英数字記号（大文字、小文字をそれぞれ1文字以上ずつ含める）
                      </FieldHelperText>
                    )}
                    {!passwordMismatchError && (
                      <FieldErrorText>
                        {errors.confirmPassword?.message}
                      </FieldErrorText>
                    )}
                  </Stack>
                </FieldRoot>
                {(!!apiError || !!passwordMismatchError) && (
                  <HStack
                    gridColumn={{ base: 'span 1', desktop: 'span 2' }}
                    gap="3"
                    justify="center"
                    color="text.error"
                  >
                    <AlertErrorIcon />
                    <Text fontWeight="normal">
                      {apiError ? apiError.message : passwordMismatchError}
                    </Text>
                  </HStack>
                )}
              </Grid>
              <Center gap="7" flexDirection="column">
                <Button
                  colorPalette="blue"
                  size="xl"
                  type="submit"
                  disabled={!isValid}
                  loading={isPending}
                  px="6"
                >
                  パスワードを再設定
                </Button>
              </Center>
            </Stack>
          </form>
        </Box>
      </Stack>
    </SimpleLogoPageContainer>
  )
}
